
import './App.css'
import { BrowserRouter, Route, Routes, useLocation, useParams } from "react-router"
import Home from './Pages/Home'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import YAML from './Layouts/YAML'
import type { SiteConfig } from '@/types/yaml/site'
import SiteContext from './state/SiteContext'
import Header from './Components/Header/Header'
import Markdown from './Layouts/Markdown'
import Footer from './Components/Footer/Footer'
import { useEffect, useLayoutEffect, type ReactElement } from 'react'
import About from './Pages/About/About'
import Feedback from './Pages/Feedback'
import ApiContext from '@/state/ApiContext'
import apiContextDefault from '@/state/apiContextDefault'
import CamerasLoader from '@/Pages/Cameras/Cameras'
import CamerasNew from './Pages/Cameras/CamerasNew'
import CameraDetail from './Pages/CameraDetail/CameraDetail'
import PageTitle from './Components/PageTitle'
import Demo from './Demo/Demo'
import CameraMediaDetail from '@/Pages/CameraDetail/CameraMediaDetail'
import { useAtom } from 'jotai'
import headerStateAtom from '@/state/headerStateAtom'
import isMobileAtom from '@/state/isMobileAtom'
import { debounce } from 'lodash-es'


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
})

const ProductDetail = (): ReactElement => {
  const { productId } = useParams();
  return <>
    <PageTitle>Products | WebCOOS</PageTitle>
    <Markdown markdownFile={`/md_content/products/${productId}.md`} />
  </>
}

const CameraDetailWrapper = (): ReactElement => {
  const { slug } = useParams();
  return <CameraDetail slug={slug!} />
}

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // Scrolls to the top (0,0) whenever the pathname changes [5, 27]
    window.scrollTo(0, 0);
  }, [pathname]);

  return null; // This component doesn't render anything
}

function App(site: SiteConfig) {
  const [headerState] = useAtom(headerStateAtom)
  const [, setIsMobile] = useAtom(isMobileAtom)
  useEffect(() => {
    const debounceSetIsMobile = debounce(function updateSize(): void {
      setIsMobile(window.matchMedia('(max-width: 850px)').matches)
    }, 200)

    window.addEventListener('resize', debounceSetIsMobile)

    return () => {
      window.removeEventListener('resize', debounceSetIsMobile)
    }
  }, [])

  return (
    <div className={`min-h-screen flex flex-col ${headerState ? 'pt-13' : 'pt-[100px]'}`}>
      <SiteContext value={site}>
        <BrowserRouter>
          <Header />
          <div className='h-full grow relative'>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/get-involved" element={<Markdown markdownFile="/md_content/get-involved.md" />} />
              <Route path="/about" element={<About />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/usage-guidelines" element={<Markdown markdownFile="/md_content/usage-guidelines.md" />} />
              <Route path="/products" element={
                <>
                  <PageTitle>Products | WebCOOS</PageTitle>
                  <Markdown markdownFile="/md_content/products.md" />
                </>
              } />
              <Route path="/products/:productId" element={<ProductDetail />} />
              <Route path="/cameras" element={<CamerasNew />} />
              <Route path="/cameras-old" element={<CamerasLoader />} />
              <Route path="/cameras/:slug" element={<CameraDetailWrapper />} />
              <Route path="/cameras/:asset_slug/service/:service_uuid/media/:element_uuid" element={<CameraMediaDetail />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/demo/:groupId/:itemId" element={<Demo />} />
            </Routes>
          </div>
          <Footer />
        </BrowserRouter>
      </SiteContext>
    </div>
  )
}

function AppPreload() {
  return <ApiContext value={apiContextDefault}>
    <QueryClientProvider client={queryClient}>
      <YAML<SiteConfig>
        yamlFile="/yaml_content/site.yaml"
        Component={App}
      />
    </QueryClientProvider>
  </ApiContext>
}

export default AppPreload
